<script setup>
// Панель смайлів для власного чату — три вкладки:
//
//   • Olsior — емоції каналу, як і раніше: 7TV, BTTV, FFZ і твічеві, з
//     пошуком і підвкладками по провайдерах. Згори — нещодавні (пам'ятаємо в
//     localStorage), бо в чаті ставлять ті самі десять смайлів.
//   • Emoji — звичайні емодзі (emoji-picker-element): пошук українською,
//     категорії, тон шкіри, свої нещодавні. Довантажуємо лише при першому
//     відкритті вкладки — бібліотека й словник емодзі важать.
//   • GIF — пошук гіфок через бекенд (backend/src/api/gif). Гіфка
//     надсилається окремим повідомленням одразу, як у Telegram.
//
// Емоцій каналу під тисячу, і тисяча картинок у сітці — це тисяча запитів
// на відкриття панелі. Тому малюємо перші LIMIT штук, а решту знаходять
// пошуком (він шукає по всьому списку).

const emit = defineEmits(['pick', 'gif'])

const config = useRuntimeConfig()
const { list, groups, load } = useEmotes()

const open = ref(false)
const section = ref('olsior') // 'olsior' | 'emoji' | 'gif'
const query = ref('')
const tab = ref('all')
const searchInput = ref(null)
const root = ref(null)

const LIMIT = 200
const RECENT_KEY = 'chat-recent-emotes'
const RECENT_MAX = 24

onMounted(load)

// --- Olsior ---
const recent = ref([])
const readRecent = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
    recent.value = Array.isArray(saved) ? saved : []
  } catch (e) {
    recent.value = []
  }
}
const remember = (name) => {
  recent.value = [name, ...recent.value.filter((n) => n !== name)].slice(0, RECENT_MAX)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.value))
  } catch (e) {}
}

const byName = computed(() => new Map(list.value.map((emote) => [emote.name, emote])))
const recentEmotes = computed(() => recent.value.map((name) => byName.value.get(name)).filter(Boolean))

const tabs = computed(() => [
  { id: 'all', label: 'Усі' },
  ...groups.value.map((group) => ({ id: group.id, label: group.label }))
])

// Вкладка звужує список, пошук — шукає по ній же. Спершу ті, що
// починаються з набраного: шукають за початком назви
const filtered = computed(() => {
  const base = tab.value === 'all' ? list.value : list.value.filter((emote) => emote.provider === tab.value)
  const needle = query.value.trim().toLowerCase()
  if (!needle) return base
  const starts = []
  const inside = []
  for (const emote of base) {
    const name = emote.name.toLowerCase()
    if (name.startsWith(needle)) starts.push(emote)
    else if (name.includes(needle)) inside.push(emote)
  }
  return [...starts, ...inside]
})

const visible = computed(() => filtered.value.slice(0, LIMIT))
const hidden = computed(() => Math.max(0, filtered.value.length - LIMIT))
const showRecent = computed(() => !query.value.trim() && tab.value === 'all' && recentEmotes.value.length > 0)

const pick = (emote) => {
  remember(emote.name)
  emit('pick', emote.name)
  // Панель лишається відкритою: смайли зазвичай ставлять по кілька
}

// --- Emoji ---
// Словник — українською (CLDR) з jsDelivr: назви й ключові слова для
// пошуку («сміх», «серце»), а не англійські
const EMOJI_DATA = 'https://cdn.jsdelivr.net/npm/emoji-picker-element-data@1.8.0/uk/cldr/data.json'
const EMOJI_I18N = {
  categoriesLabel: 'Категорії',
  emojiUnsupportedMessage: 'Ваш браузер не показує кольорові емодзі.',
  favoritesLabel: 'Улюблені',
  loadingMessage: 'Завантаження…',
  networkErrorMessage: 'Не вдалося завантажити емодзі.',
  regionLabel: 'Вибір емодзі',
  searchDescription: 'Коли є результати, стрілки вгору й вниз вибирають, Enter — вставляє.',
  searchLabel: 'Пошук емодзі',
  searchResultsLabel: 'Результати пошуку',
  skinToneDescription: 'Стрілки вгору й вниз вибирають, Enter — підтверджує.',
  skinToneLabel: 'Тон шкіри (зараз {skinTone})',
  skinTonesLabel: 'Тони шкіри',
  skinTones: ['Типовий', 'Світлий', 'Середньо-світлий', 'Середній', 'Середньо-темний', 'Темний'],
  categories: {
    custom: 'Свої',
    'smileys-emotion': 'Смайлики й емоції',
    'people-body': 'Люди й тіло',
    'animals-nature': 'Тварини й природа',
    'food-drink': 'Їжа й напої',
    'travel-places': 'Подорожі й місця',
    activities: 'Активності',
    objects: 'Предмети',
    symbols: 'Символи',
    flags: 'Прапори'
  }
}

const emojiHost = ref(null)
let emojiPicker = null

const mountEmoji = async () => {
  await nextTick()
  if (!emojiHost.value) return
  if (!emojiPicker) {
    const { Picker } = await import('emoji-picker-element')
    emojiPicker = new Picker({ locale: 'uk', dataSource: EMOJI_DATA, i18n: EMOJI_I18N })
    emojiPicker.classList.add('olsior-emoji')
    emojiPicker.addEventListener('emoji-click', (event) => {
      const unicode = event.detail?.unicode
      if (unicode) emit('pick', unicode)
    })
  }
  if (emojiPicker.parentNode !== emojiHost.value) emojiHost.value.appendChild(emojiPicker)
}

// --- GIF ---
const gifQuery = ref('')
const gifs = ref([])
const gifNext = ref(null)
const gifLoading = ref(false)
const gifEnabled = ref(true)
const gifError = ref('')
let gifTimer = null
let gifGen = 0

const loadGifs = async (more = false) => {
  if (gifLoading.value && more) return
  const mine = ++gifGen
  gifLoading.value = true
  gifError.value = ''
  try {
    const res = await $fetch(`${config.public.apiBase}/api/gifs`, {
      query: { q: gifQuery.value.trim() || undefined, pos: more ? gifNext.value : undefined }
    })
    if (mine !== gifGen) return
    gifEnabled.value = res.enabled !== false
    gifs.value = more ? [...gifs.value, ...res.results] : res.results
    gifNext.value = res.next
  } catch (e) {
    if (mine === gifGen) gifError.value = 'Пошук гіфок зараз не відповідає'
  } finally {
    if (mine === gifGen) gifLoading.value = false
  }
}

watch(gifQuery, () => {
  clearTimeout(gifTimer)
  gifTimer = setTimeout(() => loadGifs(false), 350)
})

const onGifScroll = (event) => {
  const el = event.currentTarget
  if (gifNext.value && !gifLoading.value && el.scrollTop + el.clientHeight > el.scrollHeight - 120) loadGifs(true)
}

const pickGif = (gif) => {
  emit('gif', gif.url)
  open.value = false
}

// --- Відкриття ---
const focusSearch = async () => {
  await nextTick()
  if (section.value === 'olsior') searchInput.value?.focus()
  if (section.value === 'gif') root.value?.querySelector('input[data-gif]')?.focus()
}

const setSection = (id) => {
  section.value = id
  if (id === 'emoji') mountEmoji()
  if (id === 'gif' && !gifs.value.length) loadGifs(false)
  focusSearch()
}

// Скільки місця над кнопкою до найближчого предка, що обрізає вміст
// (колонка коментарів під записом, бічна панель чату). Інакше панель
// вилазила за нього й верх обрізався
const PANEL_MAX = 380
const PANEL_MIN = 220
const panelHeight = ref(PANEL_MAX)

const measure = () => {
  const el = root.value
  if (!el) return
  const top = el.getBoundingClientRect().top
  let limit = 0
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const style = getComputedStyle(node)
    if (/(hidden|auto|scroll|clip)/.test(`${style.overflowY}${style.overflow}`)) {
      limit = Math.max(limit, node.getBoundingClientRect().top)
      break
    }
  }
  const room = top - Math.max(limit, 0) - 12
  panelHeight.value = Math.max(PANEL_MIN, Math.min(PANEL_MAX, room))
}

const toggle = () => {
  open.value = !open.value
  if (!open.value) return
  measure()
  readRecent()
  setSection(section.value)
}

const onOutside = (event) => {
  if (!open.value) return
  // Клік усередині emoji-picker приходить із його shadow DOM — шлях події
  // це бачить, а contains — ні
  if (root.value && event.composedPath().includes(root.value)) return
  open.value = false
}

const onKey = (event) => {
  if (event.key === 'Escape' && open.value) {
    open.value = false
    event.stopPropagation()
  }
}

onMounted(() => {
  document.addEventListener('mousedown', onOutside)
  document.addEventListener('keydown', onKey)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onOutside)
  document.removeEventListener('keydown', onKey)
  clearTimeout(gifTimer)
})

const SECTIONS = [
  { id: 'olsior', label: 'Olsior' },
  { id: 'emoji', label: 'Emoji' },
  { id: 'gif', label: 'GIF' }
]
</script>

<template>
  <div
    ref="root"
    class="relative"
  >
    <button
      type="button"
      class="btn btn-ghost btn-sm btn-circle"
      :class="{ 'text-primary': open }"
      title="Смайли, емодзі й гіфки"
      @click="toggle"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
        />
        <path d="M8 14s1.5 2 4 2 4-2 4-2" />
        <line
          x1="9"
          y1="9"
          x2="9.01"
          y2="9"
        />
        <line
          x1="15"
          y1="9"
          x2="15.01"
          y2="9"
        />
      </svg>
    </button>

    <div
      v-if="open"
      class="bottom-full left-0 z-50 absolute flex flex-col bg-base-200 shadow-xl mb-2 border border-base-content/10 rounded-lg w-[min(360px,88vw)] overflow-hidden"
      :style="{ height: `${panelHeight}px` }"
    >
      <!-- Розділи -->
      <div class="flex flex-shrink-0 gap-1 bg-base-300/60 p-1">
        <button
          v-for="item in SECTIONS"
          :key="item.id"
          type="button"
          class="flex-1 py-1 rounded font-semibold text-xs transition-colors"
          :class="section === item.id ? 'bg-primary text-primary-content' : 'text-base-content/70 hover:bg-base-100'"
          @click="setSection(item.id)"
        >
          {{ item.label }}
        </button>
      </div>

      <!-- Olsior: емоції каналу -->
      <template v-if="section === 'olsior'">
        <div class="flex-shrink-0 p-2 pb-1">
          <input
            ref="searchInput"
            v-model="query"
            type="text"
            placeholder="Пошук смайла каналу"
            class="input-bordered w-full h-8 text-xs input input-xs"
          />
        </div>

        <div class="flex flex-shrink-0 gap-1 px-2 pb-1 overflow-x-auto">
          <button
            v-for="item in tabs"
            :key="item.id"
            type="button"
            class="px-2 py-0.5 rounded text-[10px] whitespace-nowrap transition-colors"
            :class="
              tab === item.id
                ? 'bg-primary text-primary-content'
                : 'bg-base-300 hover:bg-base-100 text-base-content/70'
            "
            @click="tab = item.id"
          >
            {{ item.label }}
          </button>
        </div>

        <div class="p-2 overflow-y-auto grow">
          <template v-if="showRecent">
            <p class="mb-1 font-semibold text-[10px] text-base-content/50 uppercase">Нещодавні</p>
            <div class="flex flex-wrap gap-1 mb-2">
              <button
                v-for="emote in recentEmotes"
                :key="`r-${emote.name}`"
                type="button"
                class="flex justify-center items-center hover:bg-base-100 rounded w-9 h-9"
                :title="emote.name"
                @click="pick(emote)"
              >
                <img
                  :src="emote.url"
                  :alt="emote.name"
                  class="max-w-8 max-h-8 object-contain"
                  decoding="async"
                />
              </button>
            </div>
            <p class="mb-1 font-semibold text-[10px] text-base-content/50 uppercase">Усі смайли каналу</p>
          </template>

          <div class="flex flex-wrap content-start gap-1">
            <button
              v-for="emote in visible"
              :key="emote.name"
              type="button"
              class="flex justify-center items-center hover:bg-base-100 rounded w-9 h-9"
              :title="emote.name"
              @click="pick(emote)"
            >
              <img
                :src="emote.url"
                :alt="emote.name"
                class="max-w-8 max-h-8 object-contain"
                loading="lazy"
                decoding="async"
              />
            </button>
          </div>

          <p
            v-if="!visible.length"
            class="my-4 text-base-content/50 text-xs text-center"
          >
            Нічого не знайшли
          </p>
        </div>

        <p
          v-if="hidden"
          class="flex-shrink-0 bg-base-300 px-2 py-1 text-[10px] text-base-content/50 text-center"
        >
          Показано {{ visible.length }} із {{ filtered.length }} — уточніть пошук
        </p>
      </template>

      <!-- Emoji: emoji-picker-element малює себе сам -->
      <div
        v-show="section === 'emoji'"
        ref="emojiHost"
        class="min-h-0 grow"
      />

      <!-- GIF -->
      <template v-if="section === 'gif'">
        <div class="flex-shrink-0 p-2 pb-1">
          <input
            v-model="gifQuery"
            data-gif
            type="text"
            placeholder="Пошук гіфок"
            class="input-bordered w-full h-8 text-xs input input-xs"
          />
        </div>
        <div
          class="p-2 pt-1 overflow-y-auto grow"
          @scroll="onGifScroll"
        >
          <p
            v-if="!gifEnabled"
            class="my-6 px-4 text-base-content/50 text-xs text-center"
          >
            Пошук гіфок ще не налаштований на сервері.
          </p>
          <p
            v-else-if="gifError"
            class="my-6 text-error text-xs text-center"
          >
            {{ gifError }}
          </p>
          <div
            v-else
            class="gap-1 columns-2"
          >
            <button
              v-for="gif in gifs"
              :key="gif.id"
              type="button"
              class="block bg-base-300 hover:opacity-80 mb-1 rounded w-full overflow-hidden break-inside-avoid"
              :style="gif.width && gif.height ? { aspectRatio: `${gif.width} / ${gif.height}` } : null"
              @click="pickGif(gif)"
            >
              <img
                :src="gif.preview"
                alt="GIF"
                class="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </button>
          </div>
          <p
            v-if="gifEnabled && !gifLoading && !gifError && !gifs.length"
            class="my-6 text-base-content/50 text-xs text-center"
          >
            Нічого не знайшли
          </p>
          <p
            v-if="gifLoading"
            class="my-2 text-center"
          >
            <span class="loading loading-spinner loading-sm" />
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<style>
/* emoji-picker-element у кольорах теми сайту (daisyUI) */
emoji-picker.olsior-emoji {
  width: 100%;
  height: 100%;
  --background: hsl(var(--b2));
  --border-color: transparent;
  --input-border-color: hsl(var(--bc) / 0.2);
  --input-font-color: hsl(var(--bc));
  --input-placeholder-color: hsl(var(--bc) / 0.5);
  --button-hover-background: hsl(var(--b1));
  --button-active-background: hsl(var(--b3));
  --indicator-color: hsl(var(--p));
  --outline-color: hsl(var(--p));
  --category-font-color: hsl(var(--bc) / 0.6);
  --emoji-size: 1.5rem;
  --num-columns: 8;
}
</style>
